import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useAdminTable } from "@/app/useAdminTable";
import type { WorkerJobTarget } from "./useWorkerJobDetailController";
import { WorkerStatusTableSource } from "./WorkerStatusTableSource";
import { DiagnosticJobTableSource, UploadJobTableSource } from "./WorkerJobTableSource";
import { diagnosticJobsChangedEvent } from "./diagnosticSubmitWindow";
import { readWorkerFilters, writeWorkerFilters, type WorkerFilterState } from "./workerFilters";
import { diagnosticJobStatuses, readWorkerJobFilters, uploadJobStatuses } from "./workerJobFilters";
import { parseUtcMs } from "@/utils/datetime";

export const uploadJobsChangedEvent = "hatchery:upload-jobs-changed";
export type WorkerOperationsTab = "workers" | "uploads" | "diagnostics";

export function useWorkerOperationsController() {
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab");
  const tab: WorkerOperationsTab =
    requested === "uploads" || requested === "diagnostics" ? requested : "workers";
  const filters = readWorkerFilters(params);
  const setFilters = (next: WorkerFilterState) =>
    setParams((prev) => writeWorkerFilters(prev, next), { replace: true });
  const setTab = (nextTab: string) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", nextTab);
      ["page", "status", "kind", "order"].forEach((key) => next.delete(key));
      return next;
    });
  return { tab, filters, setFilters, setTab };
}

export function useWorkerStatusController() {
  const [params] = useSearchParams();
  const { liveOnly } = readWorkerFilters(params);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(null);
  const [openedJob, setOpenedJob] = useState<WorkerJobTarget | null>(null);
  const source = useMemo(
    () =>
      new WorkerStatusTableSource((row) => setSelectedWorkerId(row.worker_id), {
        initialLiveOnly: liveOnly,
      }),
    [liveOnly],
  );
  const snapshot = useAdminTable(source);
  const now = parseUtcMs(source.observedAt) ?? 0;
  return {
    source,
    snapshot,
    selectedWorkerId,
    openedJob,
    setOpenedJob,
    closeWorker: () => setSelectedWorkerId(null),
    selectedWorker: selectedWorkerId === null ? null : source.findRow(selectedWorkerId),
    liveOnly,
    now,
    summaryLabel: liveOnly ? "Live 워커" : `워커 (최근 12시간) · ${source.getLiveCount()} Live`,
  };
}

// 기존 링크의 view 값은 조회 조건에서 제외한다. 메인 목록은 타임라인만 표시한다.
function useJobQueryKey() {
  const [params] = useSearchParams();
  const query = new URLSearchParams(params);
  query.delete("view");
  return query.toString();
}

function useChangedEvent(event: string, refresh: () => Promise<void>) {
  useEffect(() => {
    const onChanged = () => {
      void refresh();
    };
    window.addEventListener(event, onChanged);
    return () => window.removeEventListener(event, onChanged);
  }, [event, refresh]);
}

export function useUploadJobsController() {
  const queryKey = useJobQueryKey();
  const [selected, setSelected] = useState<string | null>(null);
  const source = useMemo(
    () =>
      new UploadJobTableSource(
        readWorkerJobFilters(new URLSearchParams(queryKey), uploadJobStatuses),
        setSelected,
      ),
    [queryKey],
  );
  const snapshot = useAdminTable(source);
  const refresh = useCallback(() => source.refresh(), [source]);
  useChangedEvent(uploadJobsChangedEvent, refresh);
  const now = parseUtcMs(source.observedAt) ?? 0;
  return {
    source,
    snapshot,
    selected,
    setSelected,
    queryKey,
    now,
  };
}

export function useDiagnosticJobsController() {
  const queryKey = useJobQueryKey();
  const [selected, setSelected] = useState<string | null>(null);
  const source = useMemo(
    () =>
      new DiagnosticJobTableSource(
        readWorkerJobFilters(new URLSearchParams(queryKey), diagnosticJobStatuses),
        setSelected,
      ),
    [queryKey],
  );
  const snapshot = useAdminTable(source);
  const refresh = useCallback(() => source.refresh(), [source]);
  useChangedEvent(diagnosticJobsChangedEvent, refresh);
  const now = parseUtcMs(source.observedAt) ?? 0;
  return {
    source,
    snapshot,
    selected,
    setSelected,
    queryKey,
    now,
  };
}
